import React from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiMinus } from 'react-icons/fi';
import emptyBasket from "../../../images/empty_basket.svg";
import '../../../css/scrollbar.css';

/**
 * Table component to display stock data with selection and navigation
 */
const StocksTable = ({ data, selectedStocks, onStockSelect, onSelectAll }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  if (!data || data.length === 0) {
    return (
      <div className={`text-center py-24 text-n500 bg-p400 rounded-md shadow-sm`}>
        <div className="mb-4 flex justify-center">
          <img
            src={emptyBasket}
            alt="Empty Basket"
            className="empty-widget-image"
          />
        </div>
        <p className="text-[14px] font-medium">No available stocks with</p>
        <p className="text-[14px]  font-medium">required filters</p>
      </div>
    );
  }

  const allSelected = data.length > 0 && selectedStocks.size === data.length;
  const someSelected = selectedStocks.size > 0 && selectedStocks.size < data.length;

  const handleRowClick = (stock, e) => {
    // Don't navigate if clicking on checkbox
    if (e.target.type === 'checkbox') return;

    // Navigate to stock page with query params
    navigate(`/scanners/stock?code=${stock.code}&exchange=${stock.exchange}&scrip=${stock.stock}`);
  };

  const formatNumber = (num) => {
    if (num == null) return '-';
    return parseFloat(num).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatChange = (change) => {
    if (change == null) return '-';
    const value = parseFloat(change);
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const getChangeColor = (change) => {
    if (change == null) return '';
    const value = parseFloat(change);
    if (value > 0) return 'text-green-500';
    if (value < 0) return 'text-red-500';
    return '';
  };

  return (
    <div className={`rounded-[6px] overflow-hidden ${theme === 'dark'
      ? 'bg-[#0F0F0F] shadow-[0_2px_5px_1px_rgba(175,175,175,0.1)]'
      : 'bg-white shadow-[0_2px_5px_1px_rgba(87,87,87,0.1)]'
      }`}>
      <div className="overflow-x-auto overflow-y-auto custom-scrollbar max-h-[600px]" data-theme={theme}>
        <table className="w-full">
          <thead className={`sticky top-0 z-10 ${theme === 'dark' ? 'bg-[#0F0F0F]' : 'bg-white'}`}>
            <tr className={`border-b ${theme === 'dark' ? 'border-[#1F1F1F]' : 'border-[#EBEBEB]'}`}>
              <th className="px-3 py-3 text-left w-12">
                <button
                  onClick={() => onSelectAll(!allSelected)}
                  className={`w-5 h-5 rounded border flex items-center justify-center cursor-pointer ${allSelected || someSelected
                    ? 'bg-[#0075FF] border-[#0075FF]'
                    : theme === 'dark'
                      ? 'border-[#5B5B5B]'
                      : 'border-[#CFCFCF]'
                    }`}
                >
                  {allSelected ? (
                    <FiMinus className="text-white" size={14} />
                  ) : someSelected ? (
                    <FiCheck className="text-white" size={14} />
                  ) : null}
                </button>
              </th>
              <th className={`px-4 py-3 text-left text-sm font-medium text-n500 min-w-[180px]`}>
                Stock
              </th>
              <th className={`px-4 py-3 text-right text-sm font-medium text-n500 min-w-[100px]`}>
                LTP
              </th>
              <th className={`px-4 py-3 text-right text-sm font-medium text-n500 min-w-[100px]`}>
                Change
              </th>
              <th className={`px-4 py-3 text-right text-sm font-medium text-n500 min-w-[120px]`}>
                Prev. Close
              </th>
              <th className={`px-4 py-3 text-right text-sm font-medium text-n500 min-w-[120px]`}>
                Volume
              </th>
              <th className={`px-4 py-3 text-right text-sm font-medium text-n500 min-w-[100px]`}>
                Exchange
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((stock, index) => {
              const isSelected = selectedStocks.has(stock.stock);
              return (
                <tr
                  key={`${stock.stock}-${stock.exchange}-${index}`}
                  className={`border-b cursor-pointer transition-colors border-n200 ${theme === 'dark' ? 'hover:bg-gray-900' : 'hover:bg-gray-100'} ${isSelected ? 'bg-p400' : 'bg-p400'}`}
                  onClick={(e) => handleRowClick(stock, e)}
                >
                  <td className="px-3 py-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onStockSelect(stock);
                      }}
                      className={`w-5 h-5 rounded border flex items-center justify-center cursor-pointer ${isSelected
                        ? 'bg-[#0075FF] border-[#0075FF]'
                        : theme === 'dark'
                          ? 'border-[#5B5B5B]'
                          : 'border-[#CFCFCF]'
                        }`}
                    >
                      {isSelected && <FiCheck className="text-white" size={14} />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className={`text-sm font-medium text-p300`}>
                        {stock.stock}
                      </span>
                      {(stock.company_name || stock.companyName) && (
                        <span className={`text-xs text-n500`}>
                          {stock.company_name || stock.companyName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-right text-sm font-medium text-p300`}>
                    {formatNumber(stock.ltp)}
                  </td>
                  <td className={`px-4 py-3 text-right text-sm font-medium text-p300`}>
                    {formatNumber(stock.change)}
                  </td>
                  <td className={`px-4 py-3 text-right text-sm font-medium text-p300`}>
                    {formatNumber(stock.previous_close || stock.previousClose)}
                  </td>
                  <td className={`px-4 py-3 text-right text-sm font-medium text-p300`}>
                    {stock.volume ? parseFloat(stock.volume).toLocaleString('en-IN') : '-'}
                  </td>
                  <td className={`px-4 py-3 text-right`}>
                    <span className={`text-xs font-medium text-p300`}>
                      {stock.exchange}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StocksTable;
